<script setup lang="ts">
  import {
    computed,
    nextTick,
    onActivated,
    onBeforeUnmount,
    onMounted,
    reactive,
    ref,
    watch,
  } from 'vue';
  import {
    actions as actionTypes,
    useUserComponent,
    useHelpers,
    createComponentScope,
    Resource,
    uniqueID,
  } from '@integro/core';
  import type { IAppHelpers } from '@integro/core';
  import * as _ from 'lodash';

  defineOptions({
    name: 'UcContractRentalServices',
  });

  const componentMetadata = {
    kind: 'usercontrol',
  };

  const emit = defineEmits(['reload', 'close', 'save']);

  const props = defineProps({
    rental: {
      type: Object,
      required: true,
    },
  });

  const {
    $app,
    $fn,
    $i18n,
    $store,
    $config,
    $imodal,
    $notification,
    $tabs,
    $route,
    $router,
    gettext,
    pgettext,
    ngettext,
    npgettext,
    schemaDefaults,
    getSchema,
    searchDataSource,
    randomElementId,
    datetime,
    date,
    time,
    number,
    money,
    distance,
    username,
    filesize,
  } = useHelpers();

  const filterItems = (...args) => {
    const [services] = args;
    return _.filter(services, (p) => !p.inactive);
  };

  const services = ref(null);
  const ux = ref([]);

  const $data = computed(() => ({
    services: services.value,
    ux: ux.value,
  }));

  const $$load_services = async () => {
    return $fn.value.fetch('list_rental_service', {
      parameters: { rental_id: props.rental.id },
    });
  };

  const {
    loading,
    error,
    loaded,
    reloading,
    showLoadingIndicator,
    loadDataThrottled,
    friendlyName,
    loadData,
    touchLastLoaded,
    handleError,
    setData,
    forms,
    registerForm,
    unregisterForm,
    canLoadData,
    isModalComponent,
    modalName,
    pageDocument: _pageDocument,
    routeTab,
    ux: _ux,
    componentMetadata: _componentMetadata,
    $uid,
    lastLoaded,
  } = useUserComponent(props, {
    metadata: componentMetadata,
    dataSpecs: [{ name: 'services' }, { name: 'ux' }],
    loaders: { services: $$load_services },
    ux: {},
    dataRefs: { services, ux },
  });

  const $component = createComponentScope(
    {
      $app,
      $fn,
      $i18n,
      $store,
      $imodal,
      $notification,
      $tabs,
      $router,
    } as IAppHelpers,
    {
      emit,
      forms,
      error,
      isModalComponent,
      modalName,
      componentMetadata,
      loadData,
      registerForm,
      unregisterForm,
      setData,
      touchLastLoaded,
      handleError,
    }
  );

  watch(
    () => props.rental,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('rental');
    }
  );

  const act_7315092382398562_reloadComponentData_26zswp44p =
    new actionTypes.ReloadComponentDataAction({});
  const inst_evh_7315092382398562_reload_qray17776 =
    new actionTypes.EventHandlerImpl({
      action: act_7315092382398562_reloadComponentData_26zswp44p,
      event: 'reload',
      displayName: 'reloadComponentData',
    });
  const inst_evh_7315092382398562_reload = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_7315092382398562_reload_qray17776],
    event: 'reload',
  });

  const actions = {
    inst_evh_7315092382398562_reload_qray17776,
    inst_evh_7315092382398562_reload,
  };

  const evh_7315092382398562_reload_qray17776 = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_reload_qray17776.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_reload = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_reload.executeFromDOM(
      $component,
      event,
      scope
    );
  };

  defineExpose({
    loadData,
    loading,
    loaded,
    error,
  });
</script>

<template>
  <ItpUsercontrol
    :loading="loading"
    :reloading="reloading"
    :loaded="loaded"
    :error="error"
    @reload="evh_7315092382398562_reload($event, {})"
  >
    <BListGroup flush>
      <BListGroupItem
        :key="service.id"
        v-for="service in filterItems(services)"
      >
        <ItpBox direction="column">
          <ItpBox>
            <ItpText strong :text="service.service.name"> </ItpText>
            <ItpText
              :text="service.service.invoice_group_id"
              class="px-3"
              v-if="service.service.invoice_group_id"
            >
            </ItpText>
            <ItpText class="px-3">
              <ItpText>{{ date(service.valid_from) }}</ItpText>
              <ItpText v-if="service.valid_to">
                - {{ date(service.valid_to) }}</ItpText
              >
            </ItpText>
          </ItpBox>
          <ItpBox v-if="service.meter">
            <ItpText muted :text="gettext('Meter')" class="pe-3"> </ItpText>
            <UcCompactMeterDisplay :payload="service.meter">
            </UcCompactMeterDisplay>
          </ItpBox>
          <ItpBox>
            <ItpText pre v-if="service.notes">
              {{ service.notes }}
            </ItpText>
          </ItpBox>
        </ItpBox>
      </BListGroupItem>
    </BListGroup>
  </ItpUsercontrol>
</template>
